import type { QueryClient } from "@tanstack/react-query";
import { parseCompletedTransactionEntityId } from "@/api/api";
import type { SaveEntityV2Vars, V2EntityItem } from "@/services/entityOpsV2";
import type { ProjectTransactionNotification } from "@/types/api";
import type { ProjectDetailsCharacter, ProjectDetailsLocation, ProjectDetailsResponse, ProjectDetailsStoryboardShot } from "@/types/project";
import { userVideoGenPromptFromStoredPrompt } from "@/services/generation-prompts";
import { getEntityVersion, patchEntityUpsert } from "@/services/projectCachePatch";
import { bumpProjectDetailsTimestamp, setProjectDetailsIfNewer } from "@/services/projectDetails";
import {
  buildCompletedStoryboardVideoVisual,
  mergeStoryboardShotForCompletedVideo,
  patchStoryboardShotAppendPosterForGeneratedVideo,
  sceneTitleForShotSceneId,
  shotNumberFromShotId,
  storyboardShotWithGeneratedImageAtFront,
  storyboardWorkingStillImageId
} from "@/services/storyboard";
import { isOmniEditVideoModel, isOmniFamilyVideoModel } from "@/utils/videoExtend";
import { parseCompletedImageId } from "@/utils/utils";

const TRANSACTION_STATUS_COMPLETED = 1;

interface ActionStoreSlice {
  readonly projectDetails: ProjectDetailsResponse | null | undefined;
  readonly setProjectDetails: (next: ProjectDetailsResponse) => void;
}

function syncAlreadyAppliedProjectDetails(
  actionStore: ActionStoreSlice,
  latestDetails: ProjectDetailsResponse,
  tagType: string,
  tagId: string,
  mediaId: string
): ProjectDetailsResponse {
  if (!storeHasCompletedMedia(actionStore.projectDetails, tagType, tagId, mediaId)) {
    setProjectDetailsIfNewer(actionStore, latestDetails);
  }

  return latestDetails;
}

function storeHasCompletedMedia(
  details: ProjectDetailsResponse | null | undefined,
  tagType: string,
  tagId: string,
  mediaId: string
): boolean {
  const proj = details?.project;
  if (!proj) {
    return false;
  }

  if (tagType === "character") {
    const target = proj.characters?.find((character) => character.id === tagId);
    return (target?.images ?? []).includes(mediaId);
  }

  if (tagType === "location") {
    const target = proj.locations?.find((location) => location.id === tagId);
    return (target?.images ?? []).includes(mediaId);
  }

  if (tagType === "shot") {
    const target = proj["storyboard-shots"]?.find((shot) => shot.id === tagId);
    return (target?.images ?? []).includes(mediaId);
  }

  if (tagType === "video") {
    const target = proj["storyboard-shots"]?.find((shot) => shot.id === tagId);
    return (target?.visuals ?? []).some((visual) => visual.status === "completed" && visual.video_id === mediaId);
  }

  return false;
}

function parseTransactionOutputPayload(output: string | null): unknown {
  if (!output?.trim()) {
    return null;
  }

  try {
    return JSON.parse(output.trim()) as unknown;
  } catch {
    return output;
  }
}

function parseVideoEntityInfo(entityInfoJson: string | null | undefined): {
  readonly prompt?: string;
  readonly parentVideoId?: string;
  readonly referenceImageIds?: readonly string[];
  readonly duration?: number;
  readonly posterImageId?: string;
} {
  if (!entityInfoJson?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(entityInfoJson) as Record<string, unknown>;
    const parentIds = Array.isArray(parsed["parent_ids"])
      ? parsed["parent_ids"].filter((id): id is string => typeof id === "string")
      : undefined;
    const posterImageId = typeof parsed["poster_image_id"] === "string" ? parsed["poster_image_id"].trim() : "";

    return {
      prompt: typeof parsed["prompt"] === "string" ? parsed["prompt"] : undefined,
      parentVideoId:
        typeof parsed["parent_video_id"] === "string" ? parsed["parent_video_id"] : undefined,
      referenceImageIds: parentIds,
      duration: typeof parsed["duration"] === "number" ? parsed["duration"] : undefined,
      posterImageId: posterImageId === "" ? undefined : posterImageId
    };
  } catch {
    return {};
  }
}

function parseGeminiVideoRequest(request: string | null | undefined): {
  readonly prompt?: string;
  readonly duration?: number;
  readonly referenceVideoId?: string;
  readonly referenceImageIds?: readonly string[];
} {
  if (!request?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(request) as Record<string, unknown>;
    const refIds = Array.isArray(parsed["reference_image_ids"])
      ? parsed["reference_image_ids"].filter((id): id is string => typeof id === "string")
      : undefined;

    return {
      prompt: typeof parsed["prompt"] === "string" ? parsed["prompt"] : undefined,
      duration: typeof parsed["duration"] === "number" ? parsed["duration"] : undefined,
      referenceVideoId:
        typeof parsed["reference_video_id"] === "string" ? parsed["reference_video_id"] : undefined,
      referenceImageIds: refIds
    };
  } catch {
    return {};
  }
}

function videoReferenceIdsToExclude(args: {
  readonly parentVideoId?: string;
  readonly referenceVideoId?: string;
  readonly completedVideoId: string;
  readonly parentIds?: readonly string[];
  readonly videoModel: string;
}): ReadonlySet<string> {
  const ids = new Set<string>();
  const add = (id: string | undefined) => {
    const trimmed = id?.trim() ?? "";
    if (trimmed !== "") {
      ids.add(trimmed);
    }
  };

  add(args.parentVideoId);
  add(args.referenceVideoId);
  add(args.completedVideoId);

  if (isOmniEditVideoModel(args.videoModel)) {
    add(args.parentIds?.[0]);
  }

  return ids;
}

function imageReferenceIdsExcludingVideos(
  referenceIds: readonly string[],
  videoReferenceIds: ReadonlySet<string>
): readonly string[] {
  return referenceIds.filter((id) => {
    const trimmed = id.trim();
    return trimmed !== "" && !videoReferenceIds.has(trimmed);
  });
}

export function completedMediaIdFromTransaction(transaction: ProjectTransactionNotification): string | null {
  const outputPayload = parseTransactionOutputPayload(transaction.output);
  if (outputPayload !== null && typeof outputPayload === "object") {
    return parseCompletedImageId(outputPayload as Record<string, unknown>) ?? parseCompletedTransactionEntityId(outputPayload) ?? null;
  }

  return parseCompletedTransactionEntityId(outputPayload) ?? null;
}

export function isMediaGenerationTransactionApplicable(transaction: ProjectTransactionNotification): boolean {
  const tagType = transaction.tag_type?.trim().toLowerCase();
  return tagType === "character" || tagType === "location" || tagType === "shot" || tagType === "video";
}

export function isCompletedMediaGenerationTransaction(transaction: ProjectTransactionNotification): boolean {
  return transaction.transaction_status === TRANSACTION_STATUS_COMPLETED && isMediaGenerationTransactionApplicable(transaction);
}

export { oldestRequestedFirst } from "@/services/generationOrder";

export async function applyCompletedGenerationTransaction(args: {
  readonly transaction: ProjectTransactionNotification;
  readonly latestDetails: ProjectDetailsResponse;
  readonly updateProject: (vars: { readonly projectId: string; readonly body: ProjectDetailsResponse }) => Promise<ProjectDetailsResponse>;
  readonly saveCharacterV2: (vars: SaveEntityV2Vars<ProjectDetailsCharacter>) => Promise<V2EntityItem<ProjectDetailsCharacter>>;
  readonly saveLocationV2: (vars: SaveEntityV2Vars<ProjectDetailsLocation>) => Promise<V2EntityItem<ProjectDetailsLocation>>;
  readonly saveShotV2: (vars: SaveEntityV2Vars<ProjectDetailsStoryboardShot>) => Promise<V2EntityItem<ProjectDetailsStoryboardShot>>;
  readonly actionStore: ActionStoreSlice;
  readonly queryClient: QueryClient;
}): Promise<ProjectDetailsResponse | null> {
  const { transaction, latestDetails, updateProject, saveCharacterV2, saveLocationV2, saveShotV2, actionStore, queryClient } = args;

  if (!isCompletedMediaGenerationTransaction(transaction)) {
    return null;
  }

  const tagId = transaction.tag_id?.trim();
  const tagType = transaction.tag_type?.trim().toLowerCase();
  if (!tagId || !tagType) {
    return null;
  }

  const mediaId = completedMediaIdFromTransaction(transaction);
  if (!mediaId) {
    return null;
  }

  const projectId = transaction.project_id;
  const proj = latestDetails.project;
  if (!proj) {
    return null;
  }

  if (tagType === "character") {
    const characters = proj.characters ?? [];
    const targetIndex = characters.findIndex((c) => c.id === tagId);
    if (targetIndex === -1) {
      return null;
    }

    const target = characters[targetIndex]!;
    if ((target.images ?? []).includes(mediaId)) {
      return syncAlreadyAppliedProjectDetails(actionStore, latestDetails, tagType, tagId, mediaId);
    }

    const applyCharacterChange = (c: ProjectDetailsCharacter): ProjectDetailsCharacter => {
      const currentImages = c.images ?? [];
      return { ...c, images: [mediaId, ...currentImages.filter((id) => id !== mediaId)] };
    };

    const expectedCharacterVersion = getEntityVersion(latestDetails, "character", tagId);
    if (expectedCharacterVersion !== undefined) {
      const saved = await saveCharacterV2({
        projectId,
        entityId: tagId,
        currentData: target,
        applyChange: applyCharacterChange,
        expectedVersion: expectedCharacterVersion
      });
      const v2Result = bumpProjectDetailsTimestamp(patchEntityUpsert(latestDetails, "character", tagId, saved.data, saved.version));
      setProjectDetailsIfNewer(actionStore, v2Result);
      void queryClient.invalidateQueries({ queryKey: ["projectImageIds", projectId] });
      return v2Result;
    }

    const updatedCharacters = characters.map((c, index) => (index === targetIndex ? applyCharacterChange(c) : c));
    const result = await updateProject({
      projectId,
      body: {
        ...latestDetails,
        project: {
          ...proj,
          characters: updatedCharacters
        }
      }
    });
    setProjectDetailsIfNewer(actionStore, result);
    void queryClient.invalidateQueries({ queryKey: ["projectImageIds", projectId] });
    return result;
  }

  if (tagType === "location") {
    const locations = proj.locations ?? [];
    const targetIndex = locations.findIndex((l) => l.id === tagId);
    if (targetIndex === -1) {
      return null;
    }

    const target = locations[targetIndex]!;
    if ((target.images ?? []).includes(mediaId)) {
      return syncAlreadyAppliedProjectDetails(actionStore, latestDetails, tagType, tagId, mediaId);
    }

    const applyLocationChange = (l: ProjectDetailsLocation): ProjectDetailsLocation => {
      const currentImages = l.images ?? [];
      return { ...l, images: [mediaId, ...currentImages.filter((id) => id !== mediaId)] };
    };

    const expectedLocationVersion = getEntityVersion(latestDetails, "location", tagId);
    if (expectedLocationVersion !== undefined) {
      const saved = await saveLocationV2({
        projectId,
        entityId: tagId,
        currentData: target,
        applyChange: applyLocationChange,
        expectedVersion: expectedLocationVersion
      });
      const v2Result = bumpProjectDetailsTimestamp(patchEntityUpsert(latestDetails, "location", tagId, saved.data, saved.version));
      setProjectDetailsIfNewer(actionStore, v2Result);
      void queryClient.invalidateQueries({ queryKey: ["projectImageIds", projectId] });
      return v2Result;
    }

    const updatedLocations = locations.map((l, index) => (index === targetIndex ? applyLocationChange(l) : l));
    const result = await updateProject({
      projectId,
      body: {
        ...latestDetails,
        project: {
          ...proj,
          locations: updatedLocations
        }
      }
    });
    setProjectDetailsIfNewer(actionStore, result);
    void queryClient.invalidateQueries({ queryKey: ["projectImageIds", projectId] });
    return result;
  }

  if (tagType === "shot") {
    const shots = proj["storyboard-shots"] ?? [];
    const targetIndex = shots.findIndex((s) => s.id === tagId);
    if (targetIndex === -1) {
      return null;
    }

    const target = shots[targetIndex]!;
    if ((target.images ?? []).includes(mediaId)) {
      return syncAlreadyAppliedProjectDetails(actionStore, latestDetails, tagType, tagId, mediaId);
    }

    const applyShotChange = (s: ProjectDetailsStoryboardShot): ProjectDetailsStoryboardShot => storyboardShotWithGeneratedImageAtFront(s, mediaId);

    const expectedShotVersion = getEntityVersion(latestDetails, "shot", tagId);
    if (expectedShotVersion !== undefined) {
      const saved = await saveShotV2({
        projectId,
        entityId: tagId,
        currentData: target,
        applyChange: applyShotChange,
        expectedVersion: expectedShotVersion
      });
      const v2Result = bumpProjectDetailsTimestamp(patchEntityUpsert(latestDetails, "shot", tagId, saved.data, saved.version));
      setProjectDetailsIfNewer(actionStore, v2Result);
      void queryClient.invalidateQueries({ queryKey: ["projectImageIds", projectId] });
      return v2Result;
    }

    const updatedShots: ProjectDetailsStoryboardShot[] = shots.map((s, index) => (index === targetIndex ? applyShotChange(s) : s));
    const result = await updateProject({
      projectId,
      body: {
        ...latestDetails,
        project: {
          ...proj,
          "storyboard-shots": updatedShots
        }
      }
    });
    setProjectDetailsIfNewer(actionStore, result);
    void queryClient.invalidateQueries({ queryKey: ["projectImageIds", projectId] });
    return result;
  }

  if (tagType === "video") {
    const shots = proj["storyboard-shots"] ?? [];
    const targetIndex = shots.findIndex((s) => s.id === tagId);
    if (targetIndex === -1) {
      return null;
    }

    const shot = shots[targetIndex]!;
    if ((shot.visuals ?? []).some((v) => v.status === "completed" && v.video_id === mediaId)) {
      return syncAlreadyAppliedProjectDetails(actionStore, latestDetails, tagType, tagId, mediaId);
    }

    const entityInfo = parseVideoEntityInfo(transaction.entity_info_json);
    const requestInfo = parseGeminiVideoRequest(transaction.request);
    const videoModel = transaction.model_id?.trim() || "unknown";
    const requestedReferenceImageIds = [
      ...(entityInfo.referenceImageIds ?? []),
      ...(requestInfo.referenceImageIds ?? [])
    ].filter((id, index, all) => id.trim() !== "" && all.indexOf(id) === index);
    const videoReferenceIds = videoReferenceIdsToExclude({
      parentVideoId: entityInfo.parentVideoId,
      referenceVideoId: requestInfo.referenceVideoId,
      completedVideoId: mediaId,
      parentIds: entityInfo.referenceImageIds,
      videoModel
    });
    const imageReferenceIds = imageReferenceIdsExcludingVideos(requestedReferenceImageIds, videoReferenceIds);
    const { posterImageId } = entityInfo;
    const referenceImageId =
      imageReferenceIds[0] ??
      (isOmniFamilyVideoModel(videoModel) ? "" : (storyboardWorkingStillImageId(shot) ?? ""));
    const prompt = entityInfo.prompt ?? requestInfo.prompt ?? shot["video-gen-prompt"] ?? "";
    const userVideoGenPrompt = userVideoGenPromptFromStoredPrompt(prompt);
    const duration =
      entityInfo.duration ??
      requestInfo.duration ??
      (typeof shot["assumed-video-duration"] === "number" && Number.isFinite(shot["assumed-video-duration"])
        ? Math.max(1, Math.round(shot["assumed-video-duration"]))
        : 8);
    const storeShots = actionStore.projectDetails?.project?.["storyboard-shots"] ?? [];
    const storeShot = storeShots.find((s) => s.id === shot.id);
    const outputPayload = parseTransactionOutputPayload(transaction.output);
    const transactionRecord = typeof outputPayload === "object" && outputPayload !== null ? (outputPayload as Record<string, unknown>) : undefined;
    const visual = buildCompletedStoryboardVideoVisual({
      videoId: mediaId,
      transactionData: transactionRecord,
      referenceImageId,
      prompt,
      duration,
      videoModel,
      sceneName: sceneTitleForShotSceneId(proj.scenes, shot["scene-id"]),
      shotNumber: shotNumberFromShotId(shot["shot-id"]),
      storyboardShotId: shot.id
    });

    const applyVideoChange = (s: ProjectDetailsStoryboardShot): ProjectDetailsStoryboardShot => {
      let merged = mergeStoryboardShotForCompletedVideo(s, storeShot, shot, visual);
      if (userVideoGenPrompt.length > 0 && (merged["video-gen-prompt"]?.trim() ?? "") === "") {
        merged = { ...merged, "video-gen-prompt": userVideoGenPrompt };
      }

      if (isOmniFamilyVideoModel(videoModel)) {
        const sourceVideoId = entityInfo.parentVideoId ?? requestInfo.referenceVideoId;
        const orderedOmniRefs = [
          ...(typeof sourceVideoId === "string" && sourceVideoId.trim() !== "" ? [sourceVideoId.trim()] : []),
          ...requestedReferenceImageIds.filter((id) => id.trim() !== "")
        ].filter((id, index, all) => all.indexOf(id) === index);

        // Transaction metadata / request payload is the authoritative generation order — do not
        // prepend stale project-json snapshots (poster ids, partial lists) ahead of it.
        const refsToKeep =
          orderedOmniRefs.length > 0
            ? orderedOmniRefs
            : [
                ...(storeShot?.["video-uploaded-images"] ?? []),
                ...(shot["video-uploaded-images"] ?? []),
                ...(merged["video-uploaded-images"] ?? [])
              ].filter((id, index, all) => id.trim() !== "" && all.indexOf(id) === index);

        merged = { ...merged, "video-uploaded-images": refsToKeep };
      }

      if (posterImageId !== undefined) {
        merged = patchStoryboardShotAppendPosterForGeneratedVideo(merged, mediaId, posterImageId);
      }

      return merged;
    };

    const expectedVideoShotVersion = getEntityVersion(latestDetails, "shot", shot.id);
    if (expectedVideoShotVersion !== undefined) {
      const saved = await saveShotV2({
        projectId,
        entityId: shot.id,
        currentData: shot,
        applyChange: applyVideoChange,
        expectedVersion: expectedVideoShotVersion
      });
      const v2Result = bumpProjectDetailsTimestamp(patchEntityUpsert(latestDetails, "shot", shot.id, saved.data, saved.version));
      setProjectDetailsIfNewer(actionStore, v2Result);
      void queryClient.invalidateQueries({ queryKey: ["projectVideoIds", projectId] });
      return v2Result;
    }

    const updatedShots: ProjectDetailsStoryboardShot[] = shots.map((s) => (s.id === shot.id ? applyVideoChange(s) : s));
    const result = await updateProject({
      projectId,
      body: {
        ...latestDetails,
        project: {
          ...proj,
          "storyboard-shots": updatedShots
        }
      }
    });
    setProjectDetailsIfNewer(actionStore, result);
    void queryClient.invalidateQueries({ queryKey: ["projectVideoIds", projectId] });
    return result;
  }

  return null;
}
